import { useEffect, useRef, useState } from "react";
import { Cookie, X } from "lucide-react";

const KEY = "fincalc:consent:v1";

// Reads current consent without forcing re-render. Other components
// (AdSense loader, GA4) call this to decide whether to init.
export function getConsent() {
    try {
        const raw = localStorage.getItem(KEY);
        if (!raw) return null;
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function saveConsent(choice) {
    const record = {
        version: 1,
        timestamp: new Date().toISOString(),
        analytics: !!choice.analytics,
        ads: !!choice.ads,
    };
    try {
        localStorage.setItem(KEY, JSON.stringify(record));
    } catch {
        /* ignore */
    }
    // Let the rest of the app react without a full reload.
    window.dispatchEvent(new CustomEvent("fincalc:consent:changed", { detail: record }));
    return record;
}

export default function CookieConsent() {
    const [consent, setConsent] = useState(() => getConsent());
    const [showPrefs, setShowPrefs] = useState(false);
    const [prefs, setPrefs] = useState({ analytics: true, ads: true });
    const dialogRef = useRef(null);
    const firstFocusRef = useRef(null);

    useEffect(() => {
        if (consent) return;
        // Focus the first action button when banner appears
        setTimeout(() => firstFocusRef.current?.focus(), 200);
        // Escape closes (with reject-all semantics)
        const onKey = (e) => {
            if (e.key === "Escape" && !consent) {
                setConsent(saveConsent({ analytics: false, ads: false }));
            }
            if (e.key === "Tab" && dialogRef.current) {
                const focusable = dialogRef.current.querySelectorAll(
                    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
                );
                if (focusable.length === 0) return;
                const first = focusable[0];
                const last = focusable[focusable.length - 1];
                if (e.shiftKey && document.activeElement === first) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [consent]);

    if (consent) return null;

    const acceptAll = () => setConsent(saveConsent({ analytics: true, ads: true }));
    const rejectAll = () => setConsent(saveConsent({ analytics: false, ads: false }));
    const savePrefs = () => setConsent(saveConsent(prefs));

    return (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby="consent-title"
            aria-describedby="consent-desc"
            className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-md z-50 anim-fade-up"
            data-testid="cookie-consent"
        >
            <div className="bg-primary text-primary-foreground rounded-2xl shadow-2xl overflow-hidden">
                <div className="relative p-6">
                    <div className="absolute inset-0 noise opacity-40 pointer-events-none" aria-hidden />
                    <div className="relative">
                        <div className="flex items-start gap-3">
                            <Cookie className="w-5 h-5 text-accent-bright mt-0.5 shrink-0" strokeWidth={1.5} aria-hidden="true" />
                            <div className="flex-1">
                                <h3 id="consent-title" className="font-serif text-xl leading-tight">
                                    A cookie-sized conversation
                                </h3>
                                <p id="consent-desc" className="mt-2 text-sm text-primary-foreground/80 leading-relaxed">
                                    We use cookies for analytics and to show ads that keep FinCalc free.
                                    Your calculator inputs stay in your browser — we never see them.
                                    You can change this any time.
                                </p>
                            </div>
                        </div>

                        {!showPrefs ? (
                            <div className="mt-5 flex flex-wrap gap-2">
                                <button
                                    ref={firstFocusRef}
                                    onClick={acceptAll}
                                    data-testid="consent-accept-all"
                                    className="h-11 px-4 rounded-md bg-accent-bright text-primary font-medium text-sm hover:brightness-95 transition"
                                >
                                    Accept all
                                </button>
                                <button
                                    onClick={rejectAll}
                                    data-testid="consent-reject-all"
                                    className="h-11 px-4 rounded-md bg-transparent border border-primary-foreground/40 text-primary-foreground text-sm hover:border-primary-foreground/80 transition-colors"
                                >
                                    Reject all
                                </button>
                                <button
                                    onClick={() => setShowPrefs(true)}
                                    data-testid="consent-show-prefs"
                                    className="h-11 px-3 text-primary-foreground/80 text-sm hover:text-primary-foreground underline underline-offset-4 decoration-primary-foreground/30 transition-colors"
                                >
                                    Preferences
                                </button>
                            </div>
                        ) : (
                            <div className="mt-5 space-y-3">
                                <label className="flex items-start gap-3 p-3 rounded-md bg-primary-foreground/5 cursor-pointer hover:bg-primary-foreground/10 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked
                                        disabled
                                        className="mt-1 accent-accent"
                                        data-testid="consent-toggle-essential"
                                    />
                                    <div>
                                        <div className="text-sm font-medium">Essential</div>
                                        <div className="text-xs text-primary-foreground/60">
                                            Required for the site to function. Always on.
                                        </div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 p-3 rounded-md bg-primary-foreground/5 cursor-pointer hover:bg-primary-foreground/10 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={prefs.analytics}
                                        onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                                        className="mt-1 accent-accent"
                                        data-testid="consent-toggle-analytics"
                                    />
                                    <div>
                                        <div className="text-sm font-medium">Analytics</div>
                                        <div className="text-xs text-primary-foreground/60">
                                            Anonymous usage stats so we know which calculators to improve.
                                        </div>
                                    </div>
                                </label>
                                <label className="flex items-start gap-3 p-3 rounded-md bg-primary-foreground/5 cursor-pointer hover:bg-primary-foreground/10 transition-colors">
                                    <input
                                        type="checkbox"
                                        checked={prefs.ads}
                                        onChange={(e) => setPrefs({ ...prefs, ads: e.target.checked })}
                                        className="mt-1 accent-accent"
                                        data-testid="consent-toggle-ads"
                                    />
                                    <div>
                                        <div className="text-sm font-medium">Personalized ads</div>
                                        <div className="text-xs text-primary-foreground/60">
                                            Lets our ad partners show more relevant ads. Disabling still shows
                                            ads, just less tailored.
                                        </div>
                                    </div>
                                </label>
                                <div className="flex gap-2 pt-1">
                                    <button
                                        onClick={savePrefs}
                                        data-testid="consent-save-prefs"
                                        className="h-11 px-4 rounded-md bg-accent-bright text-primary font-medium text-sm hover:brightness-95 transition"
                                    >
                                        Save preferences
                                    </button>
                                    <button
                                        onClick={() => setShowPrefs(false)}
                                        className="h-11 px-3 text-primary-foreground/80 text-sm hover:text-primary-foreground"
                                    >
                                        Back
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                    <button
                        onClick={rejectAll}
                        aria-label="Close and reject non-essential cookies"
                        className="absolute top-3 right-3 min-h-tap min-w-tap grid place-items-center text-primary-foreground/60 hover:text-primary-foreground"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>
            </div>
        </div>
    );
}
